import type { KnowledgeBaseDocumentTenantAnalyticsDTO } from '../../types/lac';
import { Link } from 'react-router-dom';
import { CrawlStatusBar } from '../../features/lac/components/CrawlStatusBar';
import {
  formatDocumentSourceTypeLabel,
  formatDocumentStatusLabel,
  sortDocumentSourceTypeCounts,
  sortDocumentStatusCounts,
  tenantAnalyticsDisplayName,
} from '../../features/lac/knowledgeBaseDocumentAnalyticsUtils';
import { useI18n } from '../../i18n/useI18n';
import { resolveLacTenantPath } from '../../routes/lacPaths';
import { formatBytes, formatNumber } from '../../utils/formatUtils';
import { getFileSizeStatParts } from './lacDocumentsFileSizeStats';

export type LacDocumentsAnalyticsRowProps = {
  row: KnowledgeBaseDocumentTenantAnalyticsDTO & { tenantName: string };
  showTenant: boolean;
  fileSizeLabels: { min: string; avg: string; max: string };
  emptyValue: string;
};

export function LacDocumentsAnalyticsRow({
  row,
  showTenant,
  fileSizeLabels,
  emptyValue,
}: LacDocumentsAnalyticsRowProps) {
  const { locale, translate } = useI18n();
  const displayName = tenantAnalyticsDisplayName(row);
  const fileSizeParts = getFileSizeStatParts(
    row.fileSize,
    locale,
    fileSizeLabels,
  );
  const fileSizeAriaLabel = fileSizeParts
    .map((part) => `${part.label}: ${part.value}`)
    .join(', ');
  const statusCounts = sortDocumentStatusCounts(row.statusCounts ?? []);
  const sourceTypeCounts = sortDocumentSourceTypeCounts(
    row.sourceTypeCounts ?? [],
  ).filter((item) => (item.count ?? 0) > 0);

  return (
    <tr>
      {showTenant ? (
        <td>
          <Link
            className="lac-documents__tenant-link"
            to={resolveLacTenantPath(row.tenantName, 'documents')}
            aria-label={translate('lac.documents.openTenantDocuments', {
              name: displayName,
            })}
          >
            {displayName}
          </Link>
        </td>
      ) : null}
      <td className="lac-documents__numeric">
        {formatNumber(row.documentCount ?? 0, locale)}
      </td>
      <td className="lac-documents__numeric">
        {formatBytes(row.totalFileSizeBytes ?? 0, locale)}
      </td>
      <td>
        {fileSizeParts.length > 0 ? (
          <div
            className="lac-documents__file-size"
            aria-label={fileSizeAriaLabel}
          >
            {fileSizeParts.map((part) => (
              <span key={part.key} className="lac-documents__file-size-part">
                <span className="lac-documents__file-size-label">
                  {part.label}
                </span>
                <span className="lac-documents__file-size-value">
                  {part.value}
                </span>
              </span>
            ))}
          </div>
        ) : (
          emptyValue
        )}
      </td>
      <td>
        <CrawlStatusBar
          statusCounts={statusCounts}
          locale={locale}
          resolveStatusLabel={(status) =>
            formatDocumentStatusLabel(status, translate)
          }
          sortCounts={sortDocumentStatusCounts}
          ariaLabel={translate('lac.documents.statusBar')}
        />
      </td>
      <td>
        {sourceTypeCounts.length === 0 ? (
          emptyValue
        ) : (
          <ul className="lac-documents__source-list">
            {sourceTypeCounts.map((item) => (
              <li
                key={item.sourceType ?? 'unknown'}
                className="lac-documents__source-item"
              >
                <span className="lac-documents__source-label">
                  {formatDocumentSourceTypeLabel(item.sourceType, translate)}
                </span>
                <span className="lac-documents__source-count">
                  {formatNumber(item.count ?? 0, locale)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
}
